<template>
  <header :class="['header', 'header__sticky']">
    <div class="place-items-between container">
      <nuxt-link to="/" class="logo">
        <ClientOnly>
          <nuxt-img :src="logoSrc" alt="Paribu Logo" :height="$device.isMobile ? 12 : 20" />
          <template #fallback>
            <nuxt-img
              src="/images/paribu-support-logo-light.svg"
              alt="Paribu Logo"
              :height="$device.isMobile ? 12 : 20"
            />
          </template>
        </ClientOnly>
      </nuxt-link>

      <!-- Mobile Navigation -->
      <nav v-if="$device.isMobile" class="mobile-nav">
        <p-icon
          icon="search"
          size="xmd"
          color="fg-primary"
          class="search-icon"
          @click="showSearchOverlay"
        />

        <p-icon
          icon="hamburger"
          size="xs"
          color="fg-primary"
          class="ml-12"
          @click="toggleMobileDrawer()"
        />
      </nav>

      <!-- Desktop Navigation -->
      <div v-else class="header-extra">
        <p-advanced-search
          v-if="!isHome"
          :placeholder="$t('general.search')"
          :enable-global-shortcut="true"
        />

        <ClientOnly>
          <div v-if="isHome && !isInitializing && !isLoggedIn" class="download-app-container">
            <p-button variant="filled" class="download-app-button">
              <span>{{ $t('general.downloadApp') }}</span>
              <p-icon icon="mobile-phone" size="xmd" color="fix-white" />
            </p-button>

            <div class="qrcode-popup">
              <div class="qrcode-popup__text">
                <p class="f-body-16 t-fg-primary" v-text="$t('downloadApp.qrCodeTitle')" />
              </div>
              <div class="qrcode-popup__code">
                <div class="qrcode-popup__code-container">
                  <NuxtImg
                    src="/images/paribu-app-download.svg"
                    width="180"
                    height="180"
                    alt="Paribu App Download QR"
                  />
                  <nuxt-img
                    src="/images/border-stroke.svg"
                    class="qrcode-popup__code-border"
                    width="190"
                    height="190"
                    alt="QR Border"
                  />
                </div>
              </div>
              <div class="qrcode-popup__stores">
                <a
                  href="https://apps.apple.com/tr/app/paribu-bitcoin-al%C4%B1m-sat%C4%B1m/id1448200352?l=tr"
                  target="_blank"
                >
                  <NuxtImg src="/images/app-store.svg" width="113" height="30" alt="App Store" />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.paribu.app"
                  target="_blank"
                >
                  <NuxtImg
                    src="/images/google-play.svg"
                    width="113"
                    height="30"
                    alt="Google Play"
                  />
                </a>
              </div>
            </div>
          </div>
        </ClientOnly>

        <!--
        <div
          ref="paribuSolutionsRef"
          :class="{ 'solutions-item': 1, 'solutions-item-active': showParibuSolutionsOverlay }"
          @click="showParibuSolutionsOverlay = !showParibuSolutionsOverlay"
        >
          {{ $t('general.paribuSolutions') }}
        </div>
        <div v-show="showParibuSolutionsOverlay" class="solutions-overlay">
          <paribu-solutions :items="paribuProducts" />
        </div>
        -->

        <div
          ref="languagePopupRef"
          :class="{ 'world-icon-container': 1, 'is-open': showLangPopup }"
          @click="showLangPopup = !showLangPopup"
        >
          <p-icon icon="world" color="fg-primary" size="xmd" />

          <div class="world-icon-container--popup">
            <button
              type="button"
              class="world-icon-container--popup__btn"
              @click="changeLanguage('tr')"
            >
              {{ $t('language.turkish') }}
              <p-icon v-if="currentLocale === 'tr'" icon="check" color="fg-accent" size="sm" />
            </button>
            <button
              type="button"
              class="world-icon-container--popup__btn"
              @click="changeLanguage('en')"
            >
              {{ $t('language.english') }}
              <p-icon v-if="currentLocale === 'en'" icon="check" color="fg-accent" size="sm" />
            </button>
          </div>
        </div>

        <ClientOnly>
          <template v-if="!isInitializing && isLoggedIn">
            <div
              class="profile-icon-container"
              @mouseenter="showAccountHover = true"
              @mouseleave="showAccountHover = false"
            >
              <div class="header__profile--link">
                <div class="header__profile--wrapper">
                  <div class="header__profile">
                    <p-icon icon="user-account-profile" size="xsm" color="fg-primary" />
                  </div>
                  <AccountHover
                    v-show="showAccountHover"
                    @mouseenter="showAccountHover = true"
                    @mouseleave="showAccountHover = false"
                  />
                </div>
              </div>
            </div>
          </template>
          <!-- Initializing veya giriş yapmamış durumda Giriş Yap butonu görünür -->
          <template v-else>
            <a class="header__list--link place-items-between gap-8 px-16" :href="authLoginUrl">
              {{ $t('general.signin') }}
            </a>
          </template>

          <!-- SSR çıktısı: Sayfa ilk yüklendiğinde burası görünür -->
          <template #fallback>
            <a class="header__list--link place-items-between gap-8 px-16" :href="authLoginUrl">
              {{ $t('general.signin') }}
            </a>
          </template>
        </ClientOnly>
      </div>
    </div>

    <!-- Mobile Menu -->
    <div v-if="showMobileMenu && $device.isMobile">
      <mobile-navigation @on-close="showMobileMenu = false" />
    </div>

    <!-- Mobile Drawer -->
    <div v-show="showMobileDrawer" class="mobile-drawer-bg"></div>
    <div :class="{ 'mobile-drawer': 1, 'mobile-drawer-active': showMobileDrawer }">
      <div class="d-flex justify-content-between p-16 mb-16">
        <nuxt-link to="/" class="logo">
          <ClientOnly>
            <nuxt-img :src="logoSrc" alt="Paribu Logo" :height="$device.isMobile ? 12 : 20" />
            <template #fallback>
              <nuxt-img
                src="/images/paribu-support-logo-light.svg"
                alt="Paribu Logo"
                :height="$device.isMobile ? 12 : 20"
              />
            </template>
          </ClientOnly>
        </nuxt-link>
        <p-icon icon="x-close-delete" color="fg-primary" @click="toggleMobileDrawer()" />
      </div>
      <div class="px-16">
        <ClientOnly>
          <template v-if="!isInitializing && isLoggedIn">
            <div class="account-hover">
              <div class="account-hover__user">
                <div class="account-hover__user--card">
                  <div class="account-hover__user--card__name">{{ user.info.name }}</div>

                  <div
                    class="account-hover__user--card__verified"
                    :class="isVerified ? 't-fg-accent' : 't-fg-warning'"
                  >
                    <p-icon
                      :icon="isVerified ? 'shield-verified' : 'shield-unverified'"
                      :color="isVerified ? 'fg-accent' : 'fg-warning'"
                      size="sm"
                      class="mr-4"
                    />
                    {{ isVerified ? $t('general.verified') : 'Doğrulanmamış hesap' }}
                  </div>
                </div>

                <p-icon
                  v-if="$device.isDesktop"
                  icon="arrow-top-right"
                  color="fg-primary"
                  size="sm"
                />
              </div>
              <p-divider v-if="$device.isDesktop" size="2px" class="my-16" />
              <div v-else class="mt-16"></div>

              <div class="account-hover__items">
                <p-list :title="$t('general.signout')" hover @on-click="signOut">
                  <template #prepend>
                    <p-icon icon="exit" size="xmd" color="fg-primary" />
                  </template>
                </p-list>
                <p-list :title="$t('general.supportTickets')" hover @on-click="goToSupportRequests">
                  <template #prepend>
                    <p-icon icon="list-check" size="xmd" color="fg-primary" />
                  </template>
                  <template v-if="openTicketsCount > 0" #append>
                    <p-badge :text="openTicketsCount" color="success" size="lg" />
                  </template>
                </p-list>
                <div class="mobile-lang-toggle">
                  <p-list
                    :title="$t('general.languagePreferences')"
                    hover
                    @on-click="showMobileLangPopup = !showMobileLangPopup"
                  >
                    <template #prepend>
                      <p-icon icon="world" size="xmd" color="fg-primary" />
                    </template>
                    <template #append>
                      <p-icon
                        :icon="showMobileLangPopup ? 'chevron-up' : 'chevron-down'"
                        size="sm"
                        color="fg-secondary"
                      />
                    </template>
                  </p-list>
                  <div v-show="showMobileLangPopup" class="mobile-lang-toggle__options">
                    <button
                      type="button"
                      class="mobile-lang-toggle__btn"
                      @click="changeLanguage('tr')"
                    >
                      {{ $t('language.turkish') }}
                      <p-icon
                        v-if="currentLocale === 'tr'"
                        icon="check"
                        color="fg-accent"
                        size="sm"
                      />
                    </button>
                    <button
                      type="button"
                      class="mobile-lang-toggle__btn"
                      @click="changeLanguage('en')"
                    >
                      {{ $t('language.english') }}
                      <p-icon
                        v-if="currentLocale === 'en'"
                        icon="check"
                        color="fg-accent"
                        size="sm"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else-if="!isInitializing">
            <a :href="authLoginUrl">
              <p-button variant="filled" color="green" size="md" class="full-width">
                {{ $t('general.signin') }}
              </p-button>
            </a>

            <div class="mobile-lang-toggle account-hover__items mt-16">
              <p-list
                :title="$t('general.languagePreferences')"
                hover
                @on-click="showMobileLangPopup = !showMobileLangPopup"
              >
                <template #prepend>
                  <p-icon icon="world" size="xmd" color="fg-primary" />
                </template>
                <template #append>
                  <p-icon
                    :icon="showMobileLangPopup ? 'chevron-up' : 'chevron-down'"
                    size="sm"
                    color="fg-secondary"
                  />
                </template>
              </p-list>
              <div v-show="showMobileLangPopup" class="mobile-lang-toggle__options">
                <button type="button" class="mobile-lang-toggle__btn" @click="changeLanguage('tr')">
                  {{ $t('language.turkish') }}
                  <p-icon v-if="currentLocale === 'tr'" icon="check" color="fg-accent" size="sm" />
                </button>
                <button type="button" class="mobile-lang-toggle__btn" @click="changeLanguage('en')">
                  {{ $t('language.english') }}
                  <p-icon v-if="currentLocale === 'en'" icon="check" color="fg-accent" size="sm" />
                </button>
              </div>
            </div>
          </template>
        </ClientOnly>
        <p-divider class="mt-24" />
      </div>
      <!-- <paribu-solutions :items="paribuProducts" /> -->
    </div>

    <div v-if="isSwitchingLocale" class="locale-switch-overlay" aria-hidden="true">
      <p-loading size="lg" color="fg-accent" />
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import MobileNavigation from './MobileNavigation/index.vue'
/* import ParibuSolutions from './ParibuSolutions/ParibuSolutions.vue' */
import AccountHover from './AccountHover/AccountHover.vue'
import PAdvancedSearch from '~/components/pages/Search/PAdvancedSearch/PAdvancedSearch.vue'
import { useUserStore } from '@/store/user'
import { useHelpdeskChatStore } from '@/store/helpdeskChat'
import { usePageStore } from '@/store/page'
import { useSupportStore } from '@/store/support'
import type { SupportCategory } from '@/api/useSupport'
import { useSearchOverlay } from '@/composables/useSearchOverlay'
import { smartRouter } from '@/composables'

/* import logomark from '@/assets/images/product/logomark.svg' */

smartRouter.init()

const { locale } = useI18n()

const currentLocale = computed(() => (locale.value || '').split('-')[0])

const colorMode = useColorMode()
const logoSrc = computed(() => {
  return colorMode.value === 'dark'
    ? '/images/paribu-support-logo-dark.svg'
    : '/images/paribu-support-logo-light.svg'
})

// storeToRefs ile reaktif state/getter'ları al
const userStore = useUserStore()
const { user, isInitializing } = storeToRefs(userStore)
const isLoggedIn = computed(() => userStore.isLoggedIn)
const helpdeskChat = useHelpdeskChatStore()
const pageStore = usePageStore()
const supportStore = useSupportStore()
const isSwitchingLocale = ref(false)
const kycStatus = computed(() => (user.value.info.kyc_status || '').toString())
const isVerified = computed(() => kycStatus.value === 'verified')
const authLoginUrlBase = useRuntimeConfig().public.authLoginUrl as string
const authLoginUrl = authLoginUrlBase

const route = useRoute()

const isHome = computed(() => route.path === '/' || route.name === 'index')
const showMobileMenu = ref(false)
const showParibuSolutionsOverlay = ref(false)
const showAccountHover = ref(false)
const showMobileDrawer = ref(false)
const showLangPopup = ref(false)
const showMobileLangPopup = ref(false)
const openTicketsCount = computed(() => helpdeskChat.openTicketsCount)

const paribuSolutionsRef = ref<HTMLElement>()
const languagePopupRef = ref<HTMLElement>()

const { showSearchOverlay } = useSearchOverlay()

const handleClickOutside = (event: MouseEvent) => {
  if (paribuSolutionsRef.value && !paribuSolutionsRef.value.contains(event.target as Node)) {
    showParibuSolutionsOverlay.value = false
  }

  if (languagePopupRef.value && !languagePopupRef.value.contains(event.target as Node)) {
    showLangPopup.value = false
  }
}

// Routes whose URL is identical across locales (no translated slugs in path).
// Anything else — articles, categories, and routes with translated roots like
// /announcement <-> /duyurular — relies on locale-specific slugs and will 404
// after a locale switch, so we send the user home instead of reloading.
const LOCALE_STABLE_ROOTS = new Set(['ticket', 'health', 'categories'])

function findArticleSlugById(cats: SupportCategory[] | null, id: number): string | null {
  if (!cats || !Number.isFinite(id)) return null
  for (const cat of cats) {
    const direct = cat.articles ?? []
    const fromSub = (cat.subcategories ?? []).flatMap((sc) => sc.articles ?? [])
    const hit = [...direct, ...fromSub].find((a) => a.id === id)
    if (hit) return hit.slug
  }
  return null
}

function findCategorySlugById(cats: SupportCategory[] | null, id: number): string | null {
  if (!cats || !Number.isFinite(id)) return null
  for (const cat of cats) {
    if (cat.id === id) return cat.slug
    for (const sc of cat.subcategories ?? []) {
      if (sc.id === id) return sc.slug
    }
  }
  return null
}

const changeLanguage = async (lang: string) => {
  const normalized = (lang || '').split('-')[0] || 'tr'
  isSwitchingLocale.value = true
  useCookie('locale').value = normalized

  const router = useRouter()
  const homeHref = router.resolve('/').href

  const segments = route.path.split('/').filter(Boolean)
  const isLocaleStablePath = segments.length === 0 || LOCALE_STABLE_ROOTS.has(segments[0])

  if (isLocaleStablePath) {
    window.location.reload()
    return
  }

  const articleId = pageStore.page.currentArticleId
  const categoryId = pageStore.page.currentCategoryId

  if (articleId == null && categoryId == null) {
    window.location.href = homeHref
    return
  }

  try {
    supportStore.resetCache()
    await supportStore.fetchSupportCategories(false, normalized)
  } catch {
    window.location.href = homeHref
    return
  }

  const cats = supportStore.categories
  let nextSlug: string | null = null

  if (articleId != null) {
    nextSlug = findArticleSlugById(cats, articleId)
  } else if (categoryId != null) {
    nextSlug = findCategorySlugById(cats, categoryId)
  }

  if (!nextSlug) {
    window.location.href = homeHref
    return
  }

  window.location.href = router.resolve(`/${nextSlug}`).href
}

const toggleMobileDrawer = () => {
  showMobileDrawer.value = !showMobileDrawer.value

  if (showMobileDrawer.value) {
    document.documentElement.style.overflow = 'hidden'
  } else {
    document.documentElement.removeAttribute('style')
  }
}

function signOut(): void {
  try {
    localStorage.removeItem('auth-token')
    helpdeskChat.clearState()
  } catch {}
  window.location.reload()
}

const goToSupportRequests = () => {
  smartRouter.go('/ticket')
}

/* const paribuProducts = computed(() => [
  {
    icon: logomark,
    heading: 'Paribu',
    description: t('paribuProducts.cryptoTrading'),
    link: 'https://www.paribu.com/',
  },
  {
    icon: logomark,
    heading: 'Paribu Money',
    description: t('paribuProducts.digitalWallet'),
    link: 'https://www.paribu.com/',
  },
  {
    icon: logomark,
    heading: 'Paribu Self',
    description: t('paribuProducts.secureStorage'),
    link: 'https://www.paribu.com/self/',
  },
  {
    icon: logomark,
    heading: 'Paribu Stock',
    description: t('paribuProducts.stockInvestment'),
    link: 'https://www.paribu.com/markets',
  },
  {
    icon: logomark,
    heading: 'Paribu Custody',
    description: t('paribuProducts.institutionalCustody'),
    link: 'https://custody.paribu.com/',
  },

  {
    icon: logomark,
    heading: 'Paribu Pass',
    description: t('paribuProducts.cryptoEventTickets'),
    link: 'https://pass.paribu.com/',
  },
]) */

const fetchTickets = async () => {
  try {
    await helpdeskChat.loadTickets(1, 25)
  } catch (error) {
    // Error handled silently
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  if (isLoggedIn.value) fetchTickets()
})

watch(isLoggedIn, (loggedIn) => {
  if (loggedIn) fetchTickets()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style lang="scss" scoped>
.locale-switch-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bg-white, #fff);
  z-index: 99999;
}
</style>
